import React, { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { CardRenderContext, DeckGroupConfig, ListViewContextAction, RowFlashState } from './types'
import type { SelectionAction } from '../Table/types'
import type { ListViewCTAGroup } from './components/ListViewCard'
import { CardCtaOverlay } from './components/ListViewCard/CardCtaOverlay'
import * as s from './DeckView.css'
import { DropdownMenu, MenuItem } from '../DropdownMenu'
import { ScrollOverflowFades } from '../Table/ScrollOverflowFades'
import { useScrollOverflowEdges } from '../Table/useScrollOverflowEdges'
import { GroupSectionHeader } from './components/GroupSectionHeader'
import { useT } from "../../../app/lib/i18n";

function resolveDeckGridStyle(cardHeight?: number, cardMinHeight?: number, compact?: boolean) {
  // cardMinHeight === 0 → content-sized rows (overrides DeckView.css default 300px).
  if (cardMinHeight != null) {
    return {
      gridAutoRows: cardMinHeight <= 0 ? "auto" : `minmax(${cardMinHeight}px, auto)`,
    } as React.CSSProperties
  }
  const resolved = cardHeight ?? (compact ? 80 : undefined)
  return resolved != null ? ({ gridAutoRows: resolved } as React.CSSProperties) : undefined
}

export interface DeckViewProps<TRow extends { id: string }> {
  rows: TRow[]
  loading?: boolean
  fetching?: boolean
  renderCard: (row: TRow, ctx: CardRenderContext) => ReactNode
  group?: DeckGroupConfig<TRow>
  fillContainer?: boolean
  emptyTitle?: string
  emptyAction?: ReactNode
  onRowClick?: (row: TRow) => void
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  enableSelection?: boolean
  /** At most one row selected; no checkboxes — card click selects. */
  singleSelect?: boolean
  selectionActions?: SelectionAction[]
  getRowLabel?: (row: TRow) => string
  cardHeight?: number
  cardMinHeight?: number
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  getContextActions?: (row: TRow) => ListViewContextAction[]
  flashingRows?: Map<string, RowFlashState>
  compact?: boolean
}

interface SelectionProps {
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  enableSelection?: boolean
  singleSelect?: boolean
  selectionActions?: SelectionAction[]
  rowLabel?: string
}

interface GroupSectionProps<TRow extends { id: string }> extends SelectionProps {
  groupKey: string
  label: ReactNode
  rows: TRow[]
  defaultCollapsed: boolean
  renderCard: (row: TRow, ctx: CardRenderContext) => ReactNode
  onRowClick?: (row: TRow) => void
  getRowLabel?: (row: TRow) => string
  cardHeight?: number
  cardMinHeight?: number
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  getContextActions?: (row: TRow) => ListViewContextAction[]
  flashingRows?: Map<string, RowFlashState>
  compact?: boolean
}

function GroupSection<TRow extends { id: string }>({
  groupKey,
  label,
  rows,
  defaultCollapsed,
  renderCard,
  onRowClick,
  selectedIds,
  onSelectionChange,
  enableSelection,
  singleSelect,
  selectionActions,
  getRowLabel,
  cardHeight,
  cardMinHeight,
  getRowCtas,
  getContextActions,
  flashingRows,
  compact,
}: GroupSectionProps<TRow>) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  return (
    <section key={groupKey}>
      <GroupSectionHeader
        label={label}
        count={rows.length}
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
      />
      {!collapsed && (
        <div className={s.deckGrid} style={resolveDeckGridStyle(cardHeight, cardMinHeight, compact)}>
          {rows.map(row => (
            <CardWrapper
              key={row.id}
              row={row}
              renderCard={renderCard}
              onRowClick={onRowClick}
              selectedIds={selectedIds}
              onSelectionChange={onSelectionChange}
              enableSelection={enableSelection}
              singleSelect={singleSelect}
              selectionActions={selectionActions}
              rowLabel={getRowLabel?.(row)}
              getRowCtas={getRowCtas}
              getContextActions={getContextActions}
              flashingRows={flashingRows}
              compact={compact}
            />
          ))}
        </div>
      )}
    </section>
  )
}

interface CardMenuAction {
  id: string
  label: string
  icon?: ReactNode
  danger?: boolean
  disabled?: boolean
  disabledTooltip?: string
  onClick: () => void
}

function CardContextMenu({
  actions, onOpenChange }: { actions: CardMenuAction[]; onOpenChange?: (open: boolean) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const handleSetOpen = (v: boolean) => { setOpen(v); onOpenChange?.(v) }
  if (!actions.length) return null
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={s.deckCardMenuBtn}
        aria-label={t("shared.listView.moreOptions")}
        onClick={e => { e.stopPropagation(); handleSetOpen(!open) }}
      >
        <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={open}
        setOpen={handleSetOpen}
        onChange={v => actions.find(a => a.id === v)?.onClick()}
        closeOnSelect
      >
        {actions.map(action => (
          <MenuItem key={action.id} value={action.id} disabled={action.disabled} title={action.disabledTooltip}>
            <span className={action.danger ? s.deckCardMenuItemDanger : undefined} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {action.icon}
              {action.label}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  )
}

function CardWrapper<TRow extends { id: string }>({
  row,
  renderCard,
  onRowClick,
  selectedIds,
  onSelectionChange,
  enableSelection,
  singleSelect,
  selectionActions,
  rowLabel,
  getRowCtas,
  getContextActions,
  flashingRows,
  compact,
}: {
  row: TRow
  renderCard: (row: TRow, ctx: CardRenderContext) => ReactNode
  onRowClick?: (row: TRow) => void
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  getContextActions?: (row: TRow) => ListViewContextAction[]
  flashingRows?: Map<string, RowFlashState>
  compact?: boolean
} & SelectionProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const activatedByCardRef = useRef(false)
  const isSelected = selectedIds?.has(row.id) ?? false
  const flashInfo = flashingRows?.get(row.id)
  const showCheckbox = Boolean(enableSelection && !singleSelect)
  const singleSelectMode = Boolean(enableSelection && singleSelect)

  const selectThis = () => {
    if (!onSelectionChange) return
    onSelectionChange(new Set([row.id]))
  }

  const activate = () => {
    if (singleSelectMode) selectThis()
    onRowClick?.(row)
  }

  const ctx: CardRenderContext = {
    isSelected,
    onClick: () => {
      activatedByCardRef.current = true
      activate()
    },
    changedFields: flashInfo?.fields,
    updateGen: flashInfo?.gen,
    compact,
  }

  const toggleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    if (!onSelectionChange || !selectedIds) return
    const next = new Set(selectedIds)
    if (next.has(row.id)) next.delete(row.id)
    else next.add(row.id)
    onSelectionChange(next)
  }

  const ctaGroups = getRowCtas?.(row)

  // Prefer getContextActions (per-row unified); fall back to selectionActions for backward compat
  const menuActions: CardMenuAction[] = getContextActions
    ? getContextActions(row).map(a => ({
        id: a.id,
        label: a.label,
        icon: a.icon,
        danger: a.danger,
        disabled: a.disabled,
        disabledTooltip: a.disabledTooltip,
        onClick: () => a.onClick([row.id]),
      }))
    : (selectionActions ?? []).map((a, i) => ({
        id: String(i),
        label: a.label,
        icon: a.icon,
        danger: a.danger,
        onClick: () => a.onClick([row.id], rowLabel ? [rowLabel] : undefined),
      }))

  const wrapClass = [
    s.deckCardWrap,
    isSelected ? s.deckCardWrapSelected : '',
    menuOpen ? s.deckCardWrapMenuOpen : '',
    singleSelectMode ? s.deckCardWrapSingleSelect : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      className={wrapClass}
      onClick={singleSelectMode ? () => {
        if (activatedByCardRef.current) {
          activatedByCardRef.current = false
          return
        }
        activate()
      } : undefined}
      onKeyDown={singleSelectMode ? (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          activate()
        }
      } : undefined}
      role={singleSelectMode ? 'option' : undefined}
      aria-selected={singleSelectMode ? isSelected : undefined}
      tabIndex={singleSelectMode ? 0 : undefined}
    >
      {showCheckbox && (
        <div
          role="checkbox"
          aria-checked={isSelected}
          aria-label={isSelected ? 'Deselect' : 'Select'}
          tabIndex={0}
          className={`${s.deckCardCheckLabel}${isSelected ? ' is-checked' : ''}`}
          onClick={toggleSelect}
          onKeyDown={e => {
            if (e.key === ' ' || e.key === 'Enter') toggleSelect(e)
          }}
        >
          {isSelected && (
            <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
      )}
      {flashInfo && <div key={flashInfo.gen} className={s.updateShimmerOverlay} aria-hidden="true" />}
      <CardContextMenu actions={menuActions} onOpenChange={setMenuOpen} />
      {renderCard(row, ctx)}
      {ctaGroups?.length ? <CardCtaOverlay groups={ctaGroups} layout="deck" /> : null}
    </div>
  )
}

export function DeckView<TRow extends { id: string }>({
  rows,
  loading,
  fetching,
  renderCard,
  group,
  fillContainer = false,
  emptyTitle = 'No results',
  emptyAction,
  onRowClick,
  selectedIds,
  onSelectionChange,
  enableSelection,
  singleSelect,
  selectionActions,
  getRowLabel,
  cardHeight,
  cardMinHeight,
  getRowCtas,
  getContextActions,
  flashingRows,
  compact,
}: DeckViewProps<TRow>) {
  const wrapClass = fillContainer ? s.deckWrapFill : s.deckWrap
  const scrollRef = useRef<HTMLDivElement>(null)
  const edges = useScrollOverflowEdges(scrollRef, [rows])
  const resolvedCardHeight = cardHeight ?? (compact && cardMinHeight == null ? 80 : undefined)
  const gridStyle = resolveDeckGridStyle(resolvedCardHeight, cardMinHeight, compact && cardMinHeight == null ? compact : undefined)

  if (loading) {
    return (
      <div className={wrapClass}>
        <div className={s.deckGrid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    )
  }

  const content = (() => {
    if (rows.length === 0) {
      return (
        <div className={s.emptyNote}>
          <span>{emptyTitle}</span>
          {emptyAction ? <div className={s.emptyAction}>{emptyAction}</div> : null}
        </div>
      )
    }

    if (group) {
      const { getGroup, groupOrder, defaultCollapsed = false, renderGroupLabel } = group

      const grouped = new Map<string, TRow[]>()
      for (const row of rows) {
        const key = getGroup(row)
        if (!grouped.has(key)) grouped.set(key, [])
        grouped.get(key)!.push(row)
      }

      const orderedKeys = groupOrder
        ? [
            ...groupOrder.filter(k => grouped.has(k)),
            ...[...grouped.keys()].filter(k => !groupOrder.includes(k)),
          ]
        : [...grouped.keys()]

      return (
        <div className={s.deckGallery}>
          {orderedKeys.map(key => (
            <GroupSection
              key={key}
              groupKey={key}
              label={renderGroupLabel ? renderGroupLabel(key) : key}
              rows={grouped.get(key)!}
              defaultCollapsed={defaultCollapsed}
              renderCard={renderCard}
              onRowClick={onRowClick}
              selectedIds={selectedIds}
              onSelectionChange={onSelectionChange}
              enableSelection={enableSelection}
              singleSelect={singleSelect}
              selectionActions={selectionActions}
              getRowLabel={getRowLabel}
              cardHeight={resolvedCardHeight}
              cardMinHeight={cardMinHeight}
              getRowCtas={getRowCtas}
              getContextActions={getContextActions}
              flashingRows={flashingRows}
              compact={compact}
            />
          ))}
        </div>
      )
    }

    return (
      <div className={s.deckGrid} style={gridStyle}>
        {rows.map(row => (
          <CardWrapper
            key={row.id}
            row={row}
            renderCard={renderCard}
            onRowClick={onRowClick}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
            enableSelection={enableSelection}
            singleSelect={singleSelect}
            selectionActions={selectionActions}
            rowLabel={getRowLabel?.(row)}
            getRowCtas={getRowCtas}
            getContextActions={getContextActions}
            flashingRows={flashingRows}
            compact={compact}
          />
        ))}
      </div>
    )
  })()

  return (
    <div className={wrapClass}>
      {fetching && <div className={s.deckFetchOverlay} aria-hidden="true" />}
      {fillContainer ? (
        <>
          <div ref={scrollRef} className={s.deckScrollWrap}>{content}</div>
          <ScrollOverflowFades edges={edges} />
        </>
      ) : (
        content
      )}
    </div>
  )
}

function SkeletonCard() {
  return (
    <div
      className={s.deckCard}
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <div className={s.deckCover} style={{ opacity: 0.4 }} />
      <div className={s.deckBody} style={{ gap: 10 }}>
        {[80, 60, 100, 70].map((w, i) => (
          <div
            key={i}
            style={{
              height: 12,
              width: `${w}%`,
              borderRadius: 4,
              background: 'var(--color-border, #E5E7EB)',
            }}
          />
        ))}
      </div>
    </div>
  )
}
